import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  Tooltip,
  Badge,
  alpha
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  AccountBalance as FinanceIcon,
  AdminPanelSettings as AdminIcon,
  BarChart as AnalyticsIcon,
  EventNote as MeetingsIcon,
  CalendarMonth as WeeklyIcon,
  Grading as MarksIcon,
  FactCheck as ExamsIcon,
  Groups as StaffIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  HelpOutline as HelpIcon,
  AccessTime as WorkHoursIcon,
  EventBusy as LeaveIcon,
  Event as HolidayIcon,
  Campaign as AnnouncementsIcon,
  Article as ReportCardIcon,
  Inventory2 as InventoryIcon,
  LibraryBooks as LibraryIcon,
  Receipt as ExpensesIcon,
  FolderSpecial as DocReqIcon,
  ChildCare as DaycareIcon,
  Badge as VisitorLogIcon,
  Inbox as InboxIcon,
  CalendarMonth as StaffCalIcon,
  Download as ExportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import useInboxUnreadCount from '../hooks/useInboxUnreadCount';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 68;
const EXPANDED_GROUPS_KEY = 'classtree.sidebar.expandedGroups';

// Notification `type` values (set server-side in controllers) that count
// toward each group's badge. Only types principals actually receive belong
// here — e.g. `announcement_created` is sent to teachers, not principals,
// so it's excluded even though announcements live in this group.
const GROUP_BADGE_TYPES = {
  Overview: ['inbox_message'],
  Communication: [
    'meeting_scheduled', 'meeting_completed',
    'staff_meeting_invite', 'staff_meeting_updated', 'staff_meeting_cancelled',
  ],
};

const navGroups = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/principal/dashboard', icon: DashboardIcon },
      { name: 'Inbox', href: '/principal/inbox', icon: InboxIcon },
      { name: 'Analytics', href: '/principal/analytics', icon: AnalyticsIcon, principalOnly: true },
      { name: 'How to Use', href: '/principal/help-guide', icon: HelpIcon },
    ],
  },
  {
    label: 'People',
    items: [
      { name: 'Teachers', href: '/principal/teachers', icon: PeopleIcon },
      { name: 'Students', href: '/principal/students', icon: SchoolIcon },
      { name: 'Non-Teaching Staff', href: '/principal/non-teaching-staff', icon: StaffIcon },
      { name: 'School Admins', href: '/principal/school-admins', icon: AdminIcon, principalOnly: true },
    ],
  },
  {
    label: 'Academics',
    items: [
      { name: 'Classes', href: '/principal/classes', icon: ClassIcon },
      { name: 'Exams', href: '/principal/exams', icon: ExamsIcon, featureKey: 'academics' },
      { name: 'Marks', href: '/principal/view-marks', icon: MarksIcon, principalOnly: true, featureKey: 'academics' },
      { name: 'Report Cards', href: '/principal/report-cards', icon: ReportCardIcon, principalOnly: true, featureKey: 'academics' },
      { name: 'Weekly Plans', href: '/principal/weekly-reports', icon: WeeklyIcon, principalOnly: true, featureKey: 'academics' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'Attendance', href: '/principal/attendance', icon: AssignmentIcon, principalOnly: true },
      { name: 'Finance', href: '/principal/finance', icon: FinanceIcon },
      { name: 'Inventory', href: '/principal/inventory', icon: InventoryIcon, featureKey: 'inventory' },
      { name: 'Library', href: '/principal/library', icon: LibraryIcon, featureKey: 'library' },
      { name: 'Expenses', href: '/principal/expenses', icon: ExpensesIcon, featureKey: 'expenses', principalOnly: true },
      { name: 'Document Requirements', href: '/principal/required-documents', icon: DocReqIcon, featureKey: 'student_documents' },
      { name: 'Daycare', href: '/principal/daycare', icon: DaycareIcon },
      { name: 'Visitor Log', href: '/principal/visitor-log', icon: VisitorLogIcon, featureKey: 'visitor_log' },
      { name: 'Work Hours', href: '/principal/teachers/work-hours', icon: WorkHoursIcon, principalOnly: true },
      { name: 'Leave Management', href: '/principal/leave-management', icon: LeaveIcon, featureKey: 'leave_management' },
      { name: 'Holiday Calendar', href: '/principal/holidays', icon: HolidayIcon },
      { name: 'Export Data', href: '/principal/data-export', icon: ExportIcon, principalOnly: true },
    ],
  },
  {
    label: 'Communication',
    items: [
      { name: 'Staff Calendar', href: '/principal/staff-calendar', icon: StaffCalIcon },
      { name: 'Announcements', href: '/principal/announcements', icon: AnnouncementsIcon, featureKey: 'announcements' },
      { name: 'Parent Meetings', href: '/principal/parent-meetings', icon: MeetingsIcon, principalOnly: true },
    ],
  },
];

const PrincipalSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState(false);
  // null = no saved/computed state yet; smart per-route defaults apply below.
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_GROUPS_KEY);
      if (saved) return JSON.parse(saved);
    } catch (_) { /* ignore corrupt value */ }
    return null;
  });

  const { user, isFeatureEnabled } = useAuth();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { notifications } = useNotifications();
  const inboxUnread = useInboxUnreadCount();

  const isPrincipal = user?.role === 'principal';

  const toggleGroup = (label) => {
    setExpandedGroups(prev => {
      const next = { ...(prev || {}), [label]: !(prev?.[label] ?? true) };
      try { localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(next)); } catch (_) { /* ignore quota errors */ }
      return next;
    });
  };

  const isActive = (href) => location.pathname === href;
  const isExpanded = (label) => (expandedGroups == null ? true : expandedGroups[label] !== false);

  const groupBadgeCount = (label) => {
    const types = GROUP_BADGE_TYPES[label];
    if (!types) return 0;
    return notifications.filter(n => !n.is_read && types.includes(n.type)).length;
  };

  const filteredGroups = navGroups.map(group => ({
    ...group,
    items: group.items.filter(item =>
      (!item.principalOnly || isPrincipal) &&
      (!item.featureKey || isFeatureEnabled(item.featureKey))
    )
  })).filter(group => group.items.length > 0);

  // First-ever load (no saved preference): open Overview + whichever group
  // contains the current route, collapse the rest. Don't persist this —
  // only explicit toggles get written to localStorage, so the computed
  // defaults can still adapt if the active route changes before any toggle.
  useEffect(() => {
    if (expandedGroups !== null) return;
    const defaults = {};
    filteredGroups.forEach(group => {
      const hasActive = group.items.some(item => item.href === location.pathname);
      defaults[group.label] = group.label === 'Overview' || hasActive;
    });
    setExpandedGroups(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedGroups, location.pathname]);

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#ffffff',
        borderRight: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          px: 2,
          py: 1.5,
          minHeight: 56,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src="/assets/logo.png"
              alt="ClassTree"
              style={{ height: 28, width: 'auto', objectFit: 'contain' }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: '0.85rem' }}
            >
              ClassTree
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            sx={{ color: 'grey.500' }}
          >
            {collapsed ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Navigation Groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {filteredGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 0.5 }}>
            {!collapsed && (
              <ListItemButton
                onClick={() => toggleGroup(group.label)}
                sx={{
                  px: 2,
                  py: 0.5,
                  minHeight: 32,
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: 'grey.500',
                    letterSpacing: '1px',
                    flex: 1,
                  }}
                >
                  {group.label}
                </Typography>
                {groupBadgeCount(group.label) > 0 && (
                  <Badge
                    badgeContent={groupBadgeCount(group.label)}
                    color="error"
                    sx={{ mr: 1.5 }}
                  />
                )}
                {isExpanded(group.label) ? (
                  <ExpandLessIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                )}
              </ListItemButton>
            )}
            <Collapse in={isExpanded(group.label)} timeout="auto">
              <List disablePadding>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const isUnreadInbox = item.name === 'Inbox' && inboxUnread > 0;
                  return collapsed && !isMobile ? (
                    <Tooltip key={item.href} title={item.name} placement="right" arrow>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate(item.href);
                            if (isMobile) toggleSidebar();
                          }}
                          sx={{
                            px: 2,
                            py: 1,
                            mx: 0.5,
                            borderRadius: 2,
                            justifyContent: 'center',
                            bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            '&:hover': {
                              bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'grey.50',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              color: active ? theme.palette.primary.main : 'grey.600',
                            }}
                          >
                            {isUnreadInbox ? (
                              <Badge color="error" variant="dot">
                                <Icon fontSize="small" />
                              </Badge>
                            ) : (
                              <Icon fontSize="small" />
                            )}
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  ) : (
                    <ListItem key={item.href} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(item.href);
                          if (isMobile) toggleSidebar();
                        }}
                        sx={{
                          px: 2,
                          py: 0.75,
                          mx: 1,
                          borderRadius: 2,
                          bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          borderLeft: active ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                          '&:hover': {
                            bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'grey.50',
                          },
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: active ? theme.palette.primary.main : 'grey.600',
                          }}
                        >
                          <Icon sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isUnreadInbox ? 700 : (active ? 600 : 500),
                            color: active ? theme.palette.primary.main : 'grey.800',
                          }}
                        />
                        {isUnreadInbox && (
                          <Badge
                            badgeContent={inboxUnread}
                            color="error"
                            sx={{ mr: 1 }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* Sidebar Footer */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'grey.50',
        }}
      >
        {!collapsed ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <LockIcon sx={{ fontSize: 14, color: 'grey.500' }} />
              <Typography variant="caption" sx={{ color: 'grey.500', fontSize: '0.7rem' }}>
                Data encrypted & secured
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.7rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => window.open('mailto:support@classtree.in')}
            >
              <HelpIcon sx={{ fontSize: 14 }} />
              Need help? Contact support
            </Typography>
          </>
        ) : (
          <Tooltip title="Contact Support" placement="right">
            <IconButton
              size="small"
              onClick={() => window.open('mailto:support@classtree.in')}
              sx={{ color: 'grey.500', mx: 'auto', display: 'block' }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  // Mobile: use Drawer overlay
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={toggleSidebar}
        PaperProps={{
          sx: { width: SIDEBAR_WIDTH },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: persistent sidebar (parent row already fills the height below the header)
  return (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100%',
        transition: 'width 0.2s ease',
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default PrincipalSidebar;
